import { useEffect, Suspense, lazy } from 'react';
import { Route, Switch, Redirect } from 'wouter';
import { AuthProvider } from './contexts/AuthContext';
import { OrganizerProvider } from './contexts/OrganizerContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { initializeLocationStore } from './stores/locationStore';
import { initPWAInstallListeners } from './stores/pwaInstallStore';
import { setupManifestWatcher } from './utils/manifestSwitcher';
import { AppStoreProvider } from './components/AppStoreProvider';
import { useOfflineStatus } from './hooks/useOfflineStatus';
import OfflineMessage from './components/offline/OfflineMessage';
import InstallBanner from './components/pwa/InstallBanner';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import { initializeNormalizationCache, stopNormalizationCache } from './supabase/SmartListenerService';
import { PageLoader } from './components/common/PageLoader';
import { locationCache } from './services/LocationCacheService';
import { MAINTENANCE_MODE } from './config/maintenance.config';


// Layouts
import MobileLayout from './layouts/MobileLayout';
import LayoutWrapper from './layouts/LayoutWrapper';

// Core Pages (frequently accessed - loaded immediately)
import HomePage from './pages/HomePage/HomePage';
import { AuthPage } from './pages/Auth/AuthPage';
import AuthCallback from './pages/AuthCallback';

// Components
import PrivateRoute from './components/PrivateRoute/PrivateRoute';

// ====== LAZY LOADED PAGES ======
// Landing Page (marketing entry point)
const LandingPage = lazy(() => import('./pages/LandingPage/LandingPage'));

// Maintenance Mode (temporary — shown when MAINTENANCE_MODE flag is true)
const MaintenanceMode = lazy(() => import('./pages/Maintenance/MaintenanceMode'));

// Events Index — tourist entry point with progressive geo enhancement
const EventsIndexPage = lazy(() => import('./pages/EventsIndex/EventsIndexPage'));

// Organizer Pages (loaded only when authenticated organizer accesses them)
const OrganizerDashboard = lazy(() => import('./pages/OrganizerDashboard/OrganizerDashboard'));
const OrganizerEvents = lazy(() => import('./pages/OrganizerEvents/OrganizerEvents'));
const OrganizerProfilePrivatePage = lazy(() => import('./pages/OrganizerProfilePrivate').then(m => ({ default: m.OrganizerProfilePrivatePage })));
const OrganizerProfileEditPage = lazy(() => import('./pages/OrganizerProfileEdit').then(m => ({ default: m.OrganizerProfileEditPage })));
const SubscriptionManagePage = lazy(() => import('./pages/Subscription').then(m => ({ default: m.SubscriptionManagePage })));
const SubscriptionSuccessPage = lazy(() => import('./pages/Subscription').then(m => ({ default: m.SubscriptionSuccessPage })));
const SubscriptionCancelPage = lazy(() => import('./pages/Subscription').then(m => ({ default: m.SubscriptionCancelPage })));

// Organizer Public Profile (less frequent)
const OrganizerProfilePage = lazy(() => import('./pages/OrganizerProfile').then(m => ({ default: m.OrganizerProfilePage })));

// Become Organizer (one-time use)
const BecomeOrganizerPage = lazy(() => import('./pages/BecomeOrganizer/BecomeOrganizerPage'));

// Favorites Page
const FavoritesPage = lazy(() => import('./pages/FavoritesPage/FavoritesPage'));

// Verify Email Page
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));

// Reset Password Page (recovery link landing page — see PRD-reset-password.md)
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));

// Legal Pages (rarely accessed)
const PrivacyPolicy = lazy(() => import('./pages/Legal').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/Legal').then(m => ({ default: m.TermsOfService })));
const TermsOfSale = lazy(() => import('./pages/Legal').then(m => ({ default: m.TermsOfSale })));
const LegalNotice = lazy(() => import('./pages/Legal').then(m => ({ default: m.LegalNotice })));

// About Page (rarely accessed)
const AboutPage = lazy(() => import('./pages/About').then(m => ({ default: m.AboutPage })));

// Dev-only Pages (never in production bundle)
const DesignSystem = lazy(() => import('./pages/DesignSystem/DesignSystem'));
const DevMonitoring = lazy(() => import('./pages/DevMonitoring/DevMonitoring'));

// Import des tests Analytics et Logger en développement
if (process.env.NODE_ENV === 'development') {
  import('./utils/analyticsTestUtils');
  import('./utils/logger'); // 🧪 Expose Logger globalement pour tests
  import('./utils/adaptiveTimeout'); // 🧪 Expose AdaptiveTimeout globalement pour tests
  import('./utils/finalOptimizationCheck'); // 🧪 Expose tests finaux pour tests
}

// Ajouter l'interface pour les données préchargées
interface PreloadedData {
  location?: {
    latitude: number;
    longitude: number;
    cityName?: string;
  };
  events?: Array<any>; // ou le type exact de vos événements
  needLocation?: boolean;
}

// Étendre l'interface Window pour TypeScript
declare global {
  interface Window {
    __PRELOADED_DATA__?: PreloadedData;
  }
}

function App() {
  const { isOffline, hasCache } = useOfflineStatus();

  // ✅ INITIALISER le cache de normalisation et le store de géolocalisation
  useEffect(() => {
    initializeNormalizationCache();

    // Initialize PWA install listeners
    const cleanupPWA = initPWAInstallListeners();

    // Initialize manifest switcher (tourist vs organizer PWA)
    const cleanupManifest = setupManifestWatcher();

    // Initialiser le store de géolocalisation
    const cleanupLocationStore = initializeLocationStore();

    // 🧹 Start automatic IndexedDB cleanup scheduler (PRD-10)
    locationCache.startCleanupScheduler();

    // ✅ Dispatcher appReady dès que React est monté (pour le badge de lancement)
    if (!(window as any).__appReadyDispatched) {
      (window as any).__appReadyDispatched = true;
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('appReady'));
      });
    }

    // DEBUG : Exposer les variables d'environnement globalement
    if (process.env.NODE_ENV === 'development') {
      (window as any).debugEnv = () => {
        console.log('NODE_ENV:', process.env.NODE_ENV);
        console.log('All REACT_APP vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP')));
        console.log('SUPABASE_URL:', process.env.REACT_APP_SUPABASE_URL);
        console.log('SUPABASE_KEY present:', !!process.env.REACT_APP_SUPABASE_ANON_KEY);
      };
    }

    // Cleanup au démontage de l'app
    return () => {
      stopNormalizationCache();
      cleanupLocationStore?.();
      cleanupPWA?.();
      cleanupManifest?.();
    };
  }, []);

  return (
    <>
      {isOffline && <OfflineMessage hasCache={hasCache} />}
      <InstallBanner />
      <AuthProvider>
        <OrganizerProvider>
            <AnalyticsProvider>
              <AppStoreProvider>
                <GoogleMapsProvider>
                  <Suspense fallback={<PageLoader />}>
                  <Switch>
                    {/* Landing Page — marketing entry point (no app layout) */}
                    <Route path="/">
                      <LandingPage />
                    </Route>

                    {/* ===== Static pages — always accessible (no Supabase needed).
                         MUST stay above the maintenance catch-all below. ===== */}
                    <Route path="/privacy">
                      <PrivacyPolicy />
                    </Route>
                    <Route path="/terms">
                      <TermsOfService />
                    </Route>
                    <Route path="/sales">
                      <TermsOfSale />
                    </Route>
                    <Route path="/legal">
                      <LegalNotice />
                    </Route>
                    <Route path="/about">
                      <AboutPage />
                    </Route>
                    <Route path="/design-system">
                      <DesignSystem />
                    </Route>
                    <Route path="/dev-monitoring">
                      <DevMonitoring />
                    </Route>

                    {/* ===== MAINTENANCE MODE catch-all =====
                         When MAINTENANCE_MODE === true, every route below this
                         (tourist, organizer, auth) is replaced by <MaintenanceMode />.
                         Toggle the flag in src/config/maintenance.config.ts. */}
                    {MAINTENANCE_MODE && (
                      <Route>
                        <MaintenanceMode />
                      </Route>
                    )}

                    <Route path="/event/:id">
                      <LayoutWrapper mode="tourist">
                        <MobileLayout>
                          <HomePage />
                        </MobileLayout>
                      </LayoutWrapper>
                    </Route>
                    <Route path="/series/:seriesId">
                      <LayoutWrapper mode="tourist">
                        <MobileLayout>
                          <HomePage />
                        </MobileLayout>
                      </LayoutWrapper>
                    </Route>
                    {/* /events (no city) — EventsIndexPage with progressive geo enhancement */}
                    <Route path="/events">
                      <EventsIndexPage />
                    </Route>
                    <Route path="/events/:location">
                      <LayoutWrapper mode="tourist">
                        <MobileLayout>
                          <HomePage />
                        </MobileLayout>
                      </LayoutWrapper>
                    </Route>

                    {/* Favorites Routes - Desktop redirects to / (sidebar), Mobile shows FavoritesPage */}
                    <Route path="/favorites">
                      <LayoutWrapper mode="tourist">
                        <FavoritesPage />
                      </LayoutWrapper>
                    </Route>
                    <Route path="/favorites/event/:id">
                      <LayoutWrapper mode="tourist">
                        <FavoritesPage />
                      </LayoutWrapper>
                    </Route>
                    <Route path="/favorites/series/:seriesId">
                      <LayoutWrapper mode="tourist">
                        <FavoritesPage />
                      </LayoutWrapper>
                    </Route>
                    
                    {/* Organizer Public Profile */}
                    <Route path="/organizer-profile/:organizerId">
                      <OrganizerProfilePage />
                    </Route>

                    {/* Organizer Routes (Protected) - Desktop uses 3-column layout, Mobile uses original layout */}
                    <Route path="/organizer">
                      <PrivateRoute>
                        <LayoutWrapper mode="organizer">
                          <OrganizerDashboard />
                        </LayoutWrapper>
                      </PrivateRoute>
                    </Route>
                    <Route path="/organizer/dashboard">
                      <PrivateRoute>
                        <LayoutWrapper mode="organizer">
                          <OrganizerDashboard />
                        </LayoutWrapper>
                      </PrivateRoute>
                    </Route>
                    <Route path="/organizer/events">
                      <PrivateRoute>
                        <LayoutWrapper mode="organizer">
                          <OrganizerEvents />
                        </LayoutWrapper>
                      </PrivateRoute>
                    </Route>
                    <Route path="/organizer/profile">
                      <PrivateRoute>
                        <LayoutWrapper mode="organizer">
                          <OrganizerProfilePrivatePage />
                        </LayoutWrapper>
                      </PrivateRoute>
                    </Route>
                    {/* Route pour édition du profil (Mobile) */}
                    <Route path="/organizer/profile/edit">
                      <PrivateRoute>
                        <LayoutWrapper mode="organizer">
                          <OrganizerProfileEditPage />
                        </LayoutWrapper>
                      </PrivateRoute>
                    </Route>
                    {/* Route pour gestion abonnement (Mobile) */}
                    <Route path="/organizer/subscription">
                      <PrivateRoute>
                        <LayoutWrapper mode="organizer">
                          <SubscriptionManagePage />
                        </LayoutWrapper>
                      </PrivateRoute>
                    </Route>
                    {/* Subscription Success/Cancel Pages */}
                    <Route path="/organizer/subscription/success">
                      <PrivateRoute>
                        <SubscriptionSuccessPage />
                      </PrivateRoute>
                    </Route>
                    <Route path="/organizer/subscription/cancel">
                      <PrivateRoute>
                        <SubscriptionCancelPage />
                      </PrivateRoute>
                    </Route>
                    {/* Route pour édition d'un event spécifique (desktop) */}
                    <Route path="/organizer/:id">
                      <PrivateRoute>
                        <LayoutWrapper mode="organizer">
                          <OrganizerDashboard />
                        </LayoutWrapper>
                      </PrivateRoute>
                    </Route>

                    {/* Auth Routes */}
                    <Route path="/login">
                      <AuthPage mode="login" />
                    </Route>
                    <Route path="/register">
                      <AuthPage mode="register" />
                    </Route>
                    <Route path="/become-organizer">
                      <BecomeOrganizerPage />
                    </Route>
                    <Route path="/auth/callback">
                      <AuthCallback />
                    </Route>
                    <Route path="/callback">
                      <AuthCallback />
                    </Route>
                    <Route path="/verify-email">
                      <VerifyEmailPage />
                    </Route>
                    <Route path="/reset-password">
                      <ResetPasswordPage />
                    </Route>

                    {/* Fallback - redirect to home */}
                    <Route>
                      <Redirect to="/" />
                    </Route>
                  </Switch>
                  </Suspense>
                </GoogleMapsProvider>
              </AppStoreProvider>
            </AnalyticsProvider>
        </OrganizerProvider>
      </AuthProvider>
    </>
  );
};

export default App;
